import React, { useRef, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import SEO from "../components/SEO";
import Nav from "../components/Nav";
import Footer from "../components/Footer";

/* ─────────────────────────────────────────
   Scroll-reveal hook
───────────────────────────────────────── */
function useReveal(threshold = 0.12) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    if (!ref.current) return;
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setVisible(true); obs.disconnect(); } },
      { threshold }
    );
    obs.observe(ref.current);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, visible];
}

/* ─────────────────────────────────────────
   DATA
───────────────────────────────────────── */
const PHILOSOPHY = [
  {
    headline: "Business before trends.",
    body: "Trends come and go. The business comes first. We build what moves the business forward — not what's popular right now.",
  },
  {
    headline: "Strategy before execution.",
    body: "We want to know why something should be done before deciding how to do it. Action without direction is just noise.",
  },
  {
    headline: "Clarity before complexity.",
    body: "More doesn't always mean better. The clearest path is usually the right one.",
  },
  {
    headline: "Growth before vanity.",
    body: "Attention matters. Business impact matters more. We measure what moves the business — not what looks good in a report.",
  },
];

const PILLARS = [
  { label: "Strategy",   body: "Understanding where the business needs to go — and building the clearest path to get there.", icon: "◈" },
  { label: "Creativity", body: "Turning ideas into things people notice, remember and act on.", icon: "◉" },
  { label: "Technology", body: "Building digital experiences and systems that make the business stronger and more efficient.", icon: "◐" },
  { label: "Growth",     body: "Connecting everything to reach the right audience, at the right moment, and drive measurable action.", icon: "◎" },
];

const APPROACH_STEPS = [
  { n: "01", title: "Understand",  body: "Business, audience, goals and challenges. We ask the questions most agencies skip." },
  { n: "02", title: "Strategise",  body: "Identify what actually needs to be built. Not everything. The right things." },
  { n: "03", title: "Build",       body: "Turn the strategy into brand, digital, creative and marketing assets that work together." },
  { n: "04", title: "Grow",        body: "Launch, learn, optimise and keep moving forward. Growth is ongoing, not one-off." },
];

const WHO_FOR = [
  "New Businesses", "Growing Brands",
  "Established Businesses", "Founders",
  "Retail & D2C", "Local Businesses",
];

const ECOSYSTEM_FLOW = ["Brand", "Digital", "Content", "Growth", "Systems"];

/* ─────────────────────────────────────────
   PAGE
───────────────────────────────────────── */
export default function AboutPage() {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  const aboutSchema = {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    "@id": "https://ovarddigital.com/about#webpage",
    "url": "https://ovarddigital.com/about",
    "name": "About OVARD Digital — Digital Marketing Agency Mysore, India",
    "description": "OVARD Digital is a strategy, creative and digital company based in Mysore, India. Founded by Harshavardhana V and Sinchana V, we combine strategy, creativity and technology to help businesses grow.",
    "isPartOf": { "@id": "https://ovarddigital.com/#website" },
    "about": { "@id": "https://ovarddigital.com/#organization" },
    "breadcrumb": {
      "@type": "BreadcrumbList",
      "itemListElement": [
        { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://ovarddigital.com/" },
        { "@type": "ListItem", "position": 2, "name": "About", "item": "https://ovarddigital.com/about" }
      ]
    }
  };

  return (
    <div className="ap-page">
      <SEO
        title="About OVARD Digital — Digital Marketing Agency Mysore, India"
        description="OVARD Digital is a strategy, creative and digital company based in Mysore, India. We build brands, websites, content and marketing systems that help businesses grow. Learn about who we are and how we work."
        canonical="/about"
        schema={aboutSchema}
        breadcrumbs={[
          { name: "Home", path: "/" },
          { name: "About", path: "/about" }
        ]}
      />
      <Nav />
      <OpeningHero />
      <BeliefSection />
      <WhatOvardDoes />
      <PhilosophySection />
      <ApproachSection />
      <PillarsSection />
      <WhoForSection />
      <ServicesViewSection />
      <FoundersSection />
      <BuildingSection />
      <ClosingSection />
      <Footer />
    </div>
  );
}

/* ─────────────────────────────────────────
   1. HERO — full-width typographic, no image
───────────────────────────────────────── */
function OpeningHero() {
  const [play, setPlay] = useState(false);
  useEffect(() => { const t = setTimeout(() => setPlay(true), 60); return () => clearTimeout(t); }, []);

  return (
    <section className={"ap-hero" + (play ? " ap-hero--play" : "")}>
      <div className="ap-hero-grain" aria-hidden="true" />
      <div className="ap-hero-glow ap-hero-glow-1" aria-hidden="true" />
      <div className="ap-hero-glow ap-hero-glow-2" aria-hidden="true" />

      {/* Decorative background text */}
      <div className="ap-hero-bg-text" aria-hidden="true">STORY</div>

      {/* Single-column main */}
      <div className="container ap-hero-main">
        <div className="ap-hero-eyebrow">
          <span className="ap-hero-dot" />
          OVARD DIGITAL — OUR STORY
        </div>
        <h1 className="ap-hero-h1">
          <span className="ap-hero-line"><span>We build around</span></span>
          <span className="ap-hero-line ap-hero-line--indent"><span>businesses.</span></span>
          <span className="ap-hero-line ap-hero-line--gold"><span>Not packages.</span></span>
        </h1>
        <div className="ap-hero-rule" aria-hidden="true" />
        <p className="ap-hero-sub">
          OVARD Digital is a strategy, creative and digital company focused on building the things businesses need to grow — from brands and digital experiences to content, marketing and systems.
        </p>
      </div>

      {/* Stats strip */}
      <div className="ap-hero-stats-strip">
        <div className="container ap-hero-stats-inner">
          {[
            { num: "100%", label: "Client Focused" },
            { num: "End-to-End", label: "Approach" },
            { num: "13",   label: "Service Categories" },
            { num: "1",    label: "Goal: Your Growth" },
          ].map((s) => (
            <div className="ap-hero-stat" key={s.label}>
              <span className="ap-hero-stat-num">{s.num}</span>
              <span className="ap-hero-stat-label">{s.label}</span>
            </div>
          ))}
          <div className="ap-hero-scroll-hint">
            <span>Scroll</span>
            <span className="ap-hero-scroll-line" />
          </div>
        </div>
      </div>

    </section>
  );
}

/* ─────────────────────────────────────────
   2. BELIEF — split layout, principles card
───────────────────────────────────────── */
function BeliefSection() {
  const [ref, visible] = useReveal(0.1);
  return (
    <section className={"ap-section ap-belief" + (visible ? " ap-visible" : "")} ref={ref}>
      <div className="container ap-belief-inner">

        <div className="ap-belief-text">
          <span className="ap-eyebrow ap-fade-up" style={{ transitionDelay: "0s" }}>Our Belief</span>
          <h2 className="ap-belief-h2 ap-fade-up" style={{ transitionDelay: "0.08s" }}>
            Good marketing starts with understanding the business.
          </h2>
          <div className="ap-belief-body">
            <p className="ap-fade-up" style={{ transitionDelay: "0.16s" }}>
              Every business is different. Different customers. Different challenges. Different opportunities.
            </p>
            <p className="ap-fade-up" style={{ transitionDelay: "0.22s" }}>
              That's why we don't believe in forcing businesses into predefined packages or one-size-fits-all solutions.
            </p>
            <blockquote className="ap-belief-quote ap-fade-up" style={{ transitionDelay: "0.3s" }}>
              "We first understand the business, then figure out what needs to be built around it."
            </blockquote>
          </div>
        </div>

        {/* Principles card — replaces image */}
        <div className="ap-belief-principles ap-fade-up" style={{ transitionDelay: "0.18s" }}>
          {[
            { num: "01", text: "Business First" },
            { num: "02", text: "Strategy Led" },
            { num: "03", text: "Results Driven" },
          ].map((p) => (
            <div className="ap-belief-principle" key={p.num}>
              <span className="ap-belief-principle-num">{p.num}</span>
              <span className="ap-belief-principle-text">{p.text}</span>
            </div>
          ))}
        </div>

      </div>
    </section>
  );
}

/* ─────────────────────────────────────────
   3. WHAT OVARD ACTUALLY DOES
───────────────────────────────────────── */
function WhatOvardDoes() {
  const [ref, visible] = useReveal(0.1);
  return (
    <section className={"ap-section ap-what" + (visible ? " ap-visible" : "")} ref={ref}>
      <div className="container">
        <div className="ap-what-head">
          <span className="ap-eyebrow ap-fade-up" style={{ transitionDelay: "0s" }}>What OVARD Actually Does</span>
          <h2 className="ap-what-h2 ap-fade-up" style={{ transitionDelay: "0.08s" }}>
            One business.<br />Many moving parts.
          </h2>
        </div>

        <div className="ap-what-layout">
          <div className="ap-what-body ap-fade-up" style={{ transitionDelay: "0.14s" }}>
            <p>A brand doesn't exist separately from its website. Content doesn't exist separately from marketing. Advertising doesn't exist separately from the customer experience.</p>
            <p>Everything connects. OVARD brings those pieces together to create a stronger digital ecosystem around the business.</p>
          </div>

          {/* Connected ecosystem strip */}
          <div className="ap-ecosystem ap-fade-up" style={{ transitionDelay: "0.26s" }}>
            {ECOSYSTEM_FLOW.map((item, i) => (
              <React.Fragment key={item}>
                <div className="ap-eco-node">
                  <span className="ap-eco-num">0{i + 1}</span>
                  <span className="ap-eco-label">{item}</span>
                </div>
                {i < ECOSYSTEM_FLOW.length - 1 && (
                  <div className="ap-eco-arrow" aria-hidden="true">
                    <svg width="28" height="12" viewBox="0 0 28 12" fill="none">
                      <path d="M1 6H27M22 1L27 6L22 11" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Closing band — replaces wide image */}
        <div className="ap-what-closing ap-fade-up" style={{ transitionDelay: "0.36s" }}>
          <p className="ap-what-closing-text">
            Everything connects. That's the <span>OVARD</span> way.
          </p>
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────
   4. PHILOSOPHY — dark, typography-heavy
───────────────────────────────────────── */
function PhilosophySection() {
  const [ref, visible] = useReveal(0.08);
  return (
    <section className={"ap-section ap-philosophy" + (visible ? " ap-visible" : "")} ref={ref}>
      <div className="container">
        <span className="ap-eyebrow ap-fade-up" style={{ transitionDelay: "0s" }}>Our Philosophy</span>

        <div className="ap-phil-rows">
          {PHILOSOPHY.map((p, i) => (
            <div
              className="ap-phil-row ap-fade-up"
              key={p.headline}
              style={{ transitionDelay: `${0.1 + i * 0.1}s` }}
            >
              <span className="ap-phil-num">0{i + 1}</span>
              <div className="ap-phil-content">
                <h3 className="ap-phil-headline">{p.headline}</h3>
                <p className="ap-phil-body">{p.body}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────
   5. APPROACH
───────────────────────────────────────── */
function ApproachSection() {
  const [ref, visible] = useReveal(0.1);
  return (
    <section className={"ap-section ap-approach-sec" + (visible ? " ap-visible" : "")} ref={ref}>
      <div className="container">
        <div className="ap-approach-head">
          <span className="ap-eyebrow ap-fade-up" style={{ transitionDelay: "0s" }}>Our Approach</span>
          <h2 className="ap-approach-h2 ap-fade-up" style={{ transitionDelay: "0.08s" }}>
            We start with the business.<br />Not the service.
          </h2>
        </div>

        <div className="ap-approach-steps">
          {APPROACH_STEPS.map((s, i) => (
            <div
              className="ap-approach-step ap-fade-up"
              key={s.n}
              style={{ transitionDelay: `${0.14 + i * 0.1}s` }}
            >
              <span className="ap-approach-num">{s.n}</span>
              <h4 className="ap-approach-title">{s.title}</h4>
              <p className="ap-approach-body">{s.body}</p>
            </div>
          ))}
        </div>

        <div className="ap-approach-close ap-fade-up" style={{ transitionDelay: "0.56s" }}>
          <p>"The right solution isn't always the biggest one. It's the one the business actually needs."</p>
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────
   6. PILLARS
───────────────────────────────────────── */
function PillarsSection() {
  const [ref, visible] = useReveal(0.1);
  return (
    <section className={"ap-section ap-pillars-sec" + (visible ? " ap-visible" : "")} ref={ref}>
      <div className="container">
        <div className="ap-pillars-layout">
          <div className="ap-pillars-head">
            <span className="ap-eyebrow ap-fade-up" style={{ transitionDelay: "0s" }}>What We Bring</span>
            <h2 className="ap-pillars-h2 ap-fade-up" style={{ transitionDelay: "0.08s" }}>
              Strategy.<br />Creativity.<br />Technology.<br />Growth.
            </h2>
          </div>
          <div className="ap-pillars-grid">
            {PILLARS.map((p, i) => (
              <div
                className="ap-pillar ap-fade-up"
                key={p.label}
                style={{ transitionDelay: `${0.12 + i * 0.1}s` }}
              >
                <span className="ap-pillar-icon">{p.icon}</span>
                <h4 className="ap-pillar-label">{p.label}</h4>
                <p className="ap-pillar-body">{p.body}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────
   7. WHO FOR — no image, quote accent
───────────────────────────────────────── */
function WhoForSection() {
  const [ref, visible] = useReveal(0.1);
  return (
    <section className={"ap-section ap-who" + (visible ? " ap-visible" : "")} ref={ref}>
      <div className="container ap-who-inner">

        <div className="ap-who-left">
          <span className="ap-eyebrow ap-fade-up" style={{ transitionDelay: "0s" }}>Who OVARD Is For</span>
          <h2 className="ap-who-h2 ap-fade-up" style={{ transitionDelay: "0.08s" }}>
            Built for businesses<br />that want to move.
          </h2>
          <p className="ap-who-body ap-fade-up" style={{ transitionDelay: "0.16s" }}>
            We work with businesses that are building, changing, launching or growing — and need the digital side of the business to move with them.
          </p>
          <div className="ap-who-rule ap-fade-up" style={{ transitionDelay: "0.22s" }} aria-hidden="true" />
          <p className="ap-who-quote ap-fade-up" style={{ transitionDelay: "0.28s" }}>
            "We work with businesses ready to move — not just businesses ready to spend."
          </p>
        </div>

        <div className="ap-who-right">
          {WHO_FOR.map((item, i) => (
            <div
              className="ap-who-tag ap-fade-up"
              key={item}
              style={{ transitionDelay: `${0.1 + i * 0.07}s` }}
            >
              <span className="ap-who-tag-dot" />
              {item}
            </div>
          ))}
        </div>

      </div>
    </section>
  );
}

/* ─────────────────────────────────────────
   8. SERVICES VIEW — dark
───────────────────────────────────────── */
function ServicesViewSection() {
  const [ref, visible] = useReveal(0.1);
  return (
    <section className={"ap-section ap-services-view" + (visible ? " ap-visible" : "")} ref={ref}>
      <div className="container ap-services-view-inner">
        <div className="ap-sv-left ap-fade-up" style={{ transitionDelay: "0s" }}>
          <span className="ap-eyebrow">Our View on Services</span>
          <h2 className="ap-sv-h2">
            We don't start<br />with a package.
          </h2>
        </div>
        <div className="ap-sv-right ap-fade-up" style={{ transitionDelay: "0.14s" }}>
          <p>You don't always need everything. You need the right combination of things.</p>
          <p>Sometimes that's a brand. Sometimes it's a website. Sometimes it's content. Sometimes it's advertising. Sometimes it's a complete digital ecosystem.</p>
          <p className="ap-sv-highlight">Our job is to figure out which.</p>
          <Link to="/services" className="ap-sv-link">
            Explore all services
            <svg width="14" height="10" viewBox="0 0 14 10" fill="none">
              <path d="M8 1L13 5L8 9" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M13 5H1" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
            </svg>
          </Link>
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────
   FOUNDERS — no wide image
───────────────────────────────────────── */
function FoundersSection() {
  const [ref, visible] = useReveal(0.1);
  return (
    <section className={"ap-section ap-founders" + (visible ? " ap-visible" : "")} ref={ref}>
      <div className="container">

        <div className="ap-founders-head">
          <span className="ap-eyebrow ap-fade-up" style={{ transitionDelay: "0s" }}>The People Behind OVARD</span>
          <h2 className="ap-founders-h2 ap-fade-up" style={{ transitionDelay: "0.08s" }}>
            Our work speaks<br />before our words do.
          </h2>
          <p className="ap-founders-sub ap-fade-up" style={{ transitionDelay: "0.16s" }}>
            OVARD was built by people who believe the best way to prove what you can do is to actually do it. Every project we take on is a reflection of that belief.
          </p>
        </div>

        <div className="ap-founders-grid">
          <div className="ap-founder-card ap-fade-up" style={{ transitionDelay: "0.24s" }}>
            <div className="ap-founder-avatar">
              <span className="ap-founder-initials">HV</span>
              <div className="ap-founder-avatar-ring" />
            </div>
            <div className="ap-founder-info">
              <span className="ap-founder-role">Founder</span>
              <h3 className="ap-founder-name">Harshavardhana V</h3>
              <p className="ap-founder-bio">
                Harshavardhana built OVARD around a simple conviction — that good strategy and great execution should work as one. He leads the thinking that shapes how OVARD approaches every client and every project.
              </p>
            </div>
          </div>

          <div className="ap-founder-card ap-fade-up" style={{ transitionDelay: "0.34s" }}>
            <div className="ap-founder-avatar">
              <span className="ap-founder-initials">SV</span>
              <div className="ap-founder-avatar-ring" />
            </div>
            <div className="ap-founder-info">
              <span className="ap-founder-role">Co-Founder</span>
              <h3 className="ap-founder-name">Sinchana V</h3>
              <p className="ap-founder-bio">
                Sinchana brings the creative and operational discipline that turns ideas into finished work. She ensures that OVARD's output is always held to the standard the brand stands for.
              </p>
            </div>
          </div>
        </div>

      </div>
    </section>
  );
}

/* ─────────────────────────────────────────
   9. WHAT WE'RE BUILDING — single column
───────────────────────────────────────── */
function BuildingSection() {
  const [ref, visible] = useReveal(0.1);
  return (
    <section className={"ap-section ap-building" + (visible ? " ap-visible" : "")} ref={ref}>
      <div className="container ap-building-grid">

        <div className="ap-building-text">
          <span className="ap-eyebrow ap-fade-up" style={{ transitionDelay: "0s" }}>What We're Building</span>
          <h2 className="ap-building-h2 ap-fade-up" style={{ transitionDelay: "0.08s" }}>
            We're building OVARD<br />for the long term.
          </h2>
          <p className="ap-building-body ap-fade-up" style={{ transitionDelay: "0.16s" }}>
            A company that can sit at the intersection of business, creativity, technology and growth. Not just an agency that executes what it's given — but a partner that understands why it's being built in the first place.
          </p>
          <div className="ap-building-tags ap-fade-up" style={{ transitionDelay: "0.24s" }}>
            {["Business", "Creativity", "Technology", "Growth"].map((t, i, a) => (
              <React.Fragment key={t}>
                <span className="ap-building-tag">{t}</span>
                {i < a.length - 1 && <span className="ap-building-sep" aria-hidden="true">×</span>}
              </React.Fragment>
            ))}
          </div>

          {/* Metric boxes */}
          <div className="ap-building-metrics ap-fade-up" style={{ transitionDelay: "0.32s" }}>
            {["Brand", "Digital", "Content", "Growth"].map((m) => (
              <div className="ap-building-metric" key={m}>
                <span className="ap-building-metric-dot" aria-hidden="true" />
                <span className="ap-building-metric-label">{m}</span>
              </div>
            ))}
          </div>
        </div>

      </div>
    </section>
  );
}

/* ─────────────────────────────────────────
   10. CLOSING
───────────────────────────────────────── */
function ClosingSection() {
  const [ref, visible] = useReveal(0.15);
  return (
    <section
      className={"ap-closing" + (visible ? " ap-closing--visible" : "")}
      ref={ref}
    >
      <div className="ap-closing-grain" aria-hidden="true" />
      <div className="ap-closing-glow" aria-hidden="true" />

      <div className="container ap-closing-inner">
        <div className="ap-closing-wordmark">
          Your Brand.<br />
          <span>Our Strategy.</span><br />
          Unlimited Growth.
        </div>
        <p className="ap-closing-sub">
          Whatever you're building, let's build the right digital foundation around it.
        </p>
        <Link to="/contact" className="ap-closing-cta">
          <span>Start a conversation</span>
          <svg width="15" height="11" viewBox="0 0 15 11" fill="none">
            <path d="M9 1L14 5.5L9 10" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M14 5.5H1" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
          </svg>
        </Link>
      </div>
    </section>
  );
}
